import { forwardRef, useSyncExternalStore } from 'react'
import { cn } from '@/lib/utils'
import { useSkin } from '@/theme/skin'

const SOURCES = [64, 128, 256]

// Names whose file failed to load: every user of that name falls back to its classic icon.
const missing = new Set()
const missingListeners = new Set()
function markMissing(name) {
  if (missing.has(name)) return
  missing.add(name)
  missingListeners.forEach((listener) => listener())
}
function subscribeMissing(listener) {
  missingListeners.add(listener)
  return () => missingListeners.delete(listener)
}
function useIsMissing(name) {
  return useSyncExternalStore(subscribeMissing, () => missing.has(name), () => false)
}

/**
 * One isometric 3D icon. Decorative by default (empty alt), like the lucide
 * icons it replaces. `size` is the CSS pixel size; the browser picks the
 * rendition from the device pixel ratio.
 */
export function Icon3D({ name, size = 28, className, alt = '' }) {
  const isMissing = useIsMissing(name)
  if (isMissing) return null
  const base = import.meta.env.BASE_URL.replace(/\/$/, '')
  const srcSet = SOURCES.map((px) => `${base}/icons3d/${name}-${px}.webp ${px}w`).join(', ')
  return (
    <img
      src={`${base}/icons3d/${name}-128.webp`}
      srcSet={srcSet}
      sizes={`${size}px`}
      width={size}
      height={size}
      alt={alt}
      aria-hidden={alt ? undefined : 'true'}
      draggable="false"
      onError={() => markMissing(name)}
      className={cn('icon3d shrink-0 select-none object-contain', className)}
      style={{ width: size, height: size }}
    />
  )
}

/**
 * A drop-in for a lucide icon: the 3D icon under the Fluent 3D skin, the
 * original otherwise. Lucide sizing classes (size-4, size-5) map to the 3D
 * sizes that read at the same weight.
 */
export function withIcon3D(name, Fallback) {
  const Component = forwardRef(function SkinnedIcon({ className, ...props }, ref) {
    const skin = useSkin()
    const isMissing = useIsMissing(name)
    if (skin !== 'fluent3d' || isMissing) return <Fallback ref={ref} className={className} {...props} />
    const size = /\bsize-(5|6|7|8)\b|\bh-5\b/.test(className || '') ? 28 : /\bsize-(3\.5|4)\b|\bh-4\b/.test(className || '') ? 22 : 20
    return <Icon3D name={name} size={size} className={cn('-my-1', className?.replace(/\b(size|h|w)-[\d.]+\b/g, ''))} />
  })
  Component.displayName = `Icon3D(${name})`
  return Component
}

/**
 * Wraps a classic icon tile: the 3D icon under the Fluent 3D skin, the tile
 * (children) otherwise. Keeps card markup untouched for the classic look.
 */
export function Skinned3D({ name, size = 44, className, children }) {
  const skin = useSkin()
  const isMissing = useIsMissing(name)
  if (skin !== 'fluent3d' || isMissing) return children
  return (
    <span data-card-icon aria-hidden="true" className={cn('inline-flex shrink-0', className)}>
      <Icon3D name={name} size={size} />
    </span>
  )
}

/** The 3D icon, or `children` (the classic icon) when its file is missing. */
export function Icon3DOr({ name, size, className, children }) {
  const isMissing = useIsMissing(name)
  return isMissing ? children : <Icon3D name={name} size={size} className={className} />
}

/**
 * Draw a data-driven icon: the named 3D icon under the Fluent 3D skin, the
 * Lucide `icon` otherwise (or when the 3D file is missing). For plain-data
 * modules (settingsNav) that must stay loadable by Node build scripts and so
 * cannot import components themselves.
 */
export function Icon3DFor({ name, icon: Fallback, className, ...props }) {
  const skin = useSkin()
  const isMissing = useIsMissing(name)
  if (!name || skin !== 'fluent3d' || isMissing) return Fallback ? <Fallback className={className} {...props} /> : null
  const size = /\bsize-(5|6|7|8)\b|\bh-5\b/.test(className || '') ? 28 : /\bsize-(3\.5|4)\b|\bh-4\b/.test(className || '') ? 22 : 20
  return <Icon3D name={name} size={size} className={cn('-my-1', className?.replace(/\b(size|h|w)-[\d.]+\b/g, ''))} />
}
